import { formatOrgNodeKey, isSameUnitCode } from "@pdpp/lib-planta";
export function buildOrgTree(units) {
    return units.flatMap((unit) => {
        const unitRawId = String(unit.unitId);
        const unitNode = {
            id: `unit:${unitRawId}`,
            rawId: unitRawId,
            name: unit.unitName,
            type: "unit",
            extId: unit.extId,
            extType: unit.extType,
            countryCode: unit.countryCode,
        };
        const departmentsByDivision = (unit.departments ?? []).reduce((acc, dept) => {
            const key = dept.divisionId;
            const arr = acc.get(key);
            if (arr) {
                arr.push(dept);
            }
            else {
                acc.set(key, [dept]);
            }
            return acc;
        }, new Map());
        const divisionNodes = (unit.divisions ?? []).flatMap((div) => {
            const divisionRawId = String(div.id);
            const divisionNode = {
                id: `division:${divisionRawId}`,
                rawId: divisionRawId,
                name: div.name,
                type: "division",
                extId: div.extId,
                parentId: unitNode.id,
            };
            const departmentNodes = (departmentsByDivision.get(div.id) ?? []).map((dept) => ({
                id: `department:${String(dept.id)}`,
                rawId: String(dept.id),
                name: dept.name,
                type: "department",
                extId: dept.extId,
                parentId: divisionNode.id,
            }));
            return [divisionNode, ...departmentNodes];
        });
        return [unitNode, ...divisionNodes];
    });
}
export function getBreadCrumbs(nodes, selectedId) {
    if (!nodes.length || selectedId == null)
        return [];
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const breadcrumbs = [];
    const visited = new Set();
    let current = nodeMap.get(selectedId);
    while (current) {
        if (visited.has(current.id)) {
            console.warn("[OrgSelect] Breadcrumb cycle detected for node id %o", current.id);
            break;
        }
        visited.add(current.id);
        breadcrumbs.unshift(current);
        if (current.parentId === null || current.parentId === undefined)
            break;
        current = nodeMap.get(current.parentId);
    }
    return breadcrumbs;
}
export function getCountryDisplayName(code, locale = "en") {
    if (code == null)
        return "";
    const regionCode = code === "CHN" ? "CN" : code;
    if (!/^[A-Z]{2,3}$/i.test(regionCode))
        return code;
    try {
        return (new Intl.DisplayNames([locale], { type: "region" }).of(regionCode) ?? code);
    }
    catch (error) {
        console.warn("[OrgSelect] Unable to resolve country display name for %o: %o", regionCode, error);
        return code;
    }
}
export function getNodeDisplayLabel(node) {
    if (node.type === "country")
        return node.name;
    if (node.type === "unit")
        return `${node.name} (${node.extType}${node.extId})`;
    return `${node.name} (${node.extId})`;
}
export function getParentUnit(node, orgNodes) {
    if (!node)
        return undefined;
    if (node.type === "unit")
        return node;
    const parent = orgNodes.find((n) => n.id === node.parentId);
    return parent ? getParentUnit(parent, orgNodes) : undefined;
}
function findUnitByExternal(orgNodes, extType, extId) {
    if (extId == null || extId === "" || extType == null || extType === "") {
        return undefined;
    }
    const units = orgNodes.filter((n) => n.type === "unit" && n.extType === extType);
    return (units.find((n) => n.extId === extId) ??
        units.find((n) => isSameUnitCode(n.extId, extId)));
}
export function resolveOrgNodeKeyToNode(key, orgNodes) {
    const unitNode = findUnitByExternal(orgNodes, key.unitType, key.unitCode);
    if (!unitNode)
        return undefined;
    const divisionNode = key.divisionCode == null
        ? undefined
        : orgNodes.find((n) => n.type === "division" &&
            n.extId === key.divisionCode &&
            n.parentId === unitNode.id);
    const departmentNode = divisionNode === undefined || key.departmentCode == null
        ? undefined
        : orgNodes.find((n) => n.type === "department" &&
            n.extId === key.departmentCode &&
            n.parentId === divisionNode.id);
    return departmentNode ?? divisionNode ?? unitNode;
}
export function buildOrgNodeKey(node, orgNodes) {
    const unitNode = getParentUnit(node, orgNodes);
    if (node === undefined ||
        unitNode?.extId == null ||
        unitNode.extId === "" ||
        unitNode.extType == null ||
        unitNode.extType === "") {
        return undefined;
    }
    const departmentNode = node.type === "department" ? node : undefined;
    const divisionNode = node.type === "division"
        ? node
        : orgNodes.find((n) => n.id === departmentNode?.parentId);
    return formatOrgNodeKey({
        unitType: unitNode.extType,
        unitCode: unitNode.extId,
        divisionCode: divisionNode?.extId,
        departmentCode: departmentNode?.extId,
    });
}
export function toForecastSelection(node, orgNodes) {
    if (node === undefined)
        return {};
    const unitNode = getParentUnit(node, orgNodes);
    const unitId = unitNode ? Number(unitNode.rawId) : undefined;
    if (node.type === "unit")
        return { unitId };
    const divisionNode = node.type === "division"
        ? node
        : orgNodes.find((n) => n.id === node.parentId && n.type === "division");
    return {
        unitId,
        divisionId: divisionNode ? Number(divisionNode.rawId) : undefined,
    };
}
//# sourceMappingURL=orgSelectHandler.js.map